import {
    authConfigStore,
    FeedbackForm,
    FormType,
} from "@amzn/aquila-feedback-web-assets";
import type { FormData } from "@amzn/aquila-service-type-script-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Flex, Icon, Popover, Text } from "@amzn/storm-ui";
import {
    thumbsDownSolid,
    thumbsUpSolid,
    thumbsDown,
    thumbsUp,
    type IconDefinition,
} from "@amzn/storm-ui-icons";
import { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useStore } from "zustand";

import { TELEMETRY_ID } from "../../constants/telemetry";
import { authStore } from "../../store/auth";
import { AquilaThumbsFeedbackPlacement } from "../../types/aquila";
import { AquilaThumbsFeedbackReaction } from "../../types/aquila";
import LocalStorageManager from "../../utils/localStorage.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import SkeletonLoader from "../common/SkeletonLoader";

const bounceAnimation = keyframes`
    0%, 100% { 
        transform: scale(1);
    }
    40% { 
        transform: scale(1.3);
    }
    60% { 
        transform: scale(0.9);
    }
    80% { 
        transform: scale(1.1);
    }
`;

const StyledAquilaThumbsFeedback = styled(Flex)`
    position: relative;
    gap: 2px;
    align-items: center;
`;

const ThumbsFeedbackIcon = styled(Icon)<{
    selected: boolean;
    disabled: boolean;
}>`
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    color: ${(props) =>
        props.selected
            ? ({ theme }) => theme.color.lightBlue[700]
            : ({ theme }) => theme.color.gray[700]};
    padding: 0px 2px;
    font-size: 1rem;
    transform-origin: center;
    will-change: transform;

    ${(props) =>
        !props.disabled &&
        css`
            &:hover {
                color: ${({ theme }) => theme.color.lightBlue[600]};
            }
        `}

    ${(props) =>
        props.selected &&
        css`
            animation-name: ${bounceAnimation};
            animation-duration: 0.6s;
            animation-timing-function: cubic-bezier(0.28, 0.84, 0.42, 1);
            animation-fill-mode: both;
        `}
`;

const StyledFeedbackPopper = styled(Popover)`
    z-index: 1001;
    > div > div {
        padding: 0;
    }
`;

const StyledFormWrapper = styled.div`
    min-width: 450px;
    min-height: 200px;
`;

const StyledText = styled(Text)`
    margin-right: 8px;
`;

const StyledSkeletionWrapper = styled(Flex)`
    flex-direction: column;
    gap: 8px;
    padding: 8px;
`;

const StyledBackdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
`;

export interface AdditionalSubmitInfo {
    feedbackReaction: AquilaThumbsFeedbackReaction;
    formData: FormData[];
    metadata?: { [key: string]: any };
}

interface AquilaThumbsFeedbackProps {
    configId?: string;
    identifier: string;
    insightType: InsightType;
    placement: AquilaThumbsFeedbackPlacement;
    metadata?: { [key: string]: any };
    onSubmit?: (
        identifier: string,
        insightType: InsightType,
        placement: AquilaThumbsFeedbackPlacement,
        additionalInfo: AdditionalSubmitInfo,
    ) => void;
}

const AnimatedIcon = ({ selected, ...props }: any) => {
    const [key, setKey] = useState(0);

    useEffect(() => {
        if (selected) {
            setKey((prev) => prev + 1);
        }
    }, [selected]);

    return <ThumbsFeedbackIcon key={key} selected={selected} {...props} />;
};

export const AquilaThumbsFeedback = (
    props: AquilaThumbsFeedbackProps,
): JSX.Element => {
    const { configId, identifier, insightType, placement, metadata, onSubmit } =
        props;
    const uniqueIdentifier = `${insightType}-${identifier}`;
    const aquilaConfig = useStore(
        authStore,
        (state) => state.settings.aquilaConfig,
    );
    const username = useStore(authStore, (state) => state.username);
    const [feedbackReaction, setFeedbackReaction] =
        useState<AquilaThumbsFeedbackReaction>(
            AquilaThumbsFeedbackReaction.NONE,
        );
    const [feedbackGiven, setFeedbackGiven] = useState<boolean>(false);
    const [isThumbsFeedbackOpen, setIsThumbsFeedbackOpen] = useState(false);
    const feedbackItem = LocalStorageManager.getWithExpiry(
        uniqueIdentifier,
    ) as any;
    const isFeedbackGiven = !!feedbackItem || feedbackGiven;
    const currentConfigId =
        configId ?? aquilaConfig.generalThumbsFeedbackConfig.configId;
    const isSidePanelPlacement =
        placement === AquilaThumbsFeedbackPlacement.SIDE_PANEL;
    const isCardPlacement = placement === AquilaThumbsFeedbackPlacement.CARD;

    const handleThumbsClick = (reaction: AquilaThumbsFeedbackReaction) => {
        if (isFeedbackGiven) return;

        setFeedbackReaction(reaction);
        setIsThumbsFeedbackOpen(true);
        logWithTakt(TELEMETRY_ID.THUMBS_FEEDBACK_CLICKED, {
            identifier,
            insightType,
            placement,
            feedbackReaction: reaction,
            metadata,
        });
    };

    const handleOnClose = () => {
        setIsThumbsFeedbackOpen(false);
        logWithTakt(TELEMETRY_ID.THUMBS_FEEDBACK_CANCELLED, {
            identifier,
            insightType,
            placement,
            feedbackReaction,
            metadata,
        });
        LocalStorageManager.storeWithExpiry(
            uniqueIdentifier,
            {
                feedbackReaction,
            },
            30,
        );
        setFeedbackGiven(true);
    };

    const handleOnSubmit = (formData: FormData[], callback, additionalInfo) => {
        LocalStorageManager.storeWithExpiry(
            uniqueIdentifier,
            {
                feedbackReaction,
            },
            30,
        );
        logWithTakt(TELEMETRY_ID.THUMBS_FEEDBACK_SUBMITTED, {
            identifier,
            insightType,
            placement,
            feedbackReaction,
            metadata: {
                ...metadata,
                rawFieldValues: additionalInfo?.rawData,
            },
        });
        if (onSubmit) {
            onSubmit(identifier, insightType, placement, {
                feedbackReaction,
                formData,
                metadata,
            });
        }
        setIsThumbsFeedbackOpen(false);
        setFeedbackGiven(true);
    };

    const isThumbsUpSelected =
        feedbackReaction === AquilaThumbsFeedbackReaction.THUMBS_UP ||
        feedbackItem?.feedbackReaction ===
            AquilaThumbsFeedbackReaction.THUMBS_UP;

    const isThumbsDownSelected =
        feedbackReaction === AquilaThumbsFeedbackReaction.THUMBS_DOWN ||
        feedbackItem?.feedbackReaction ===
            AquilaThumbsFeedbackReaction.THUMBS_DOWN;

    return (
        <>
            {isThumbsFeedbackOpen && isCardPlacement && <StyledBackdrop />}
            <StyledAquilaThumbsFeedback
                data-testid={`thumbs-feedback-${uniqueIdentifier}`}
            >
                <StyledFeedbackPopper
                    isOpen={isThumbsFeedbackOpen}
                    withCloseButton={false}
                    trigger={
                        <>
                            {[
                                AquilaThumbsFeedbackPlacement.SIDE_PANEL,
                                AquilaThumbsFeedbackPlacement.DECK_DECORATION_WITH_METADATA,
                            ].includes(placement) && (
                                <StyledText inline={true}>
                                    Was this helpful?
                                </StyledText>
                            )}
                            <AnimatedIcon
                                type={
                                    (isThumbsUpSelected
                                        ? thumbsUpSolid
                                        : thumbsUp) as IconDefinition
                                }
                                selected={isThumbsUpSelected}
                                disabled={isFeedbackGiven}
                                onClick={() =>
                                    handleThumbsClick(
                                        AquilaThumbsFeedbackReaction.THUMBS_UP,
                                    )
                                }
                                data-testid="aquila-thumbs-up"
                                aria-label="Thumbs up"
                            />
                            <AnimatedIcon
                                type={
                                    (isThumbsDownSelected
                                        ? thumbsDownSolid
                                        : thumbsDown) as IconDefinition
                                }
                                selected={isThumbsDownSelected}
                                disabled={isFeedbackGiven}
                                onClick={() =>
                                    handleThumbsClick(
                                        AquilaThumbsFeedbackReaction.THUMBS_DOWN,
                                    )
                                }
                                data-testid="aquila-thumbs-down"
                                aria-label="Thumbs down"
                            />
                        </>
                    }
                    position={isSidePanelPlacement ? "bottom" : undefined}
                >
                    <StyledFormWrapper>
                        {authConfigStore.getState().hasCredentials() && (
                            <FeedbackForm
                                isOpen={isThumbsFeedbackOpen}
                                formType={FormType.INLINE}
                                configId={currentConfigId}
                                initialValues={{
                                    feedbackReaction,
                                }}
                                username={username}
                                onSubmit={handleOnSubmit}
                                onClose={handleOnClose}
                                loaderNode={
                                    <StyledSkeletionWrapper>
                                        <SkeletonLoader
                                            height="32px"
                                            width="450px"
                                        />
                                        <SkeletonLoader
                                            height="100px"
                                            width="450px"
                                        />
                                        <SkeletonLoader
                                            height="60px"
                                            width="450px"
                                        />
                                    </StyledSkeletionWrapper>
                                }
                            />
                        )}
                    </StyledFormWrapper>
                </StyledFeedbackPopper>
            </StyledAquilaThumbsFeedback>
        </>
    );
};
